import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCrmLeadConversations,
  getCrmContactWhatsappThread,
  markCrmContactWhatsappRead,
  sendCrmLeadWhatsapp,
} from '../config/api';

// Present a 10-digit number as +91 XXXXX XXXXX; pass anything else through.
const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return value || '';
  return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
};

const formatTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const initial = (name, phone) =>
  String(name || phone || 'U').trim().charAt(0).toUpperCase();

/**
 * Two-pane WhatsApp conversation view for a lead.
 * - List of contacts (messageable) with last message preview + unread badge.
 * - Click a contact to open its thread (chat bubbles) with a compose box.
 * Mirrors the Periskope layout: inbox on the left, conversation inside.
 */
const CrmWhatsappConversations = ({
  leadId,
  hubLinks = [],
  preferredHubId,
  contacts = [],
  openContactId = null,
  onOpenedContact,
}) => {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [showNewPicker, setShowNewPicker] = useState(false);

  const [activeContact, setActiveContact] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState('');

  const [hubId, setHubId] = useState(() => {
    const preferredHub = hubLinks.find(
      (link) => Number(link.hubId) === Number(preferredHubId)
    );
    if (preferredHub) return String(preferredHub.hubId);
    return hubLinks.length === 1 ? String(hubLinks[0].hubId) : '';
  });
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const threadEndRef = useRef(null);

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    setListError('');
    try {
      const res = await getCrmLeadConversations(leadId);
      const data = res?.data || res;
      setConversations(data?.conversations || []);
    } catch (err) {
      setListError(err.message || 'Could not load conversations.');
      setConversations([]);
    } finally {
      setLoadingList(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadThread = useCallback(
    async (contactId) => {
      setLoadingThread(true);
      setThreadError('');
      try {
        const res = await getCrmContactWhatsappThread(leadId, contactId);
        const data = res?.data || res;
        setMessages(data?.messages || []);
      } catch (err) {
        setThreadError(err.message || 'Could not load the conversation.');
        setMessages([]);
      } finally {
        setLoadingThread(false);
      }
    },
    [leadId]
  );

  // Scroll to newest message whenever the thread changes.
  useEffect(() => {
    if (activeContact) {
      threadEndRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages, activeContact]);

  // Deep-link: when the parent asks to open a specific contact (e.g. a click on
  // a WhatsApp row in the mixed timeline), open that thread once conversations
  // have loaded, then clear the request.
  useEffect(() => {
    if (openContactId == null || loadingList) return;
    const target =
      conversations.find((c) => String(c.id) === String(openContactId)) ||
      (contacts || []).find((c) => String(c.id) === String(openContactId));
    if (target) {
      setActiveContact({ ...target, unreadCount: target.unreadCount || 0 });
      setSendError('');
      setDraft('');
      loadThread(target.id);
      if (target.unreadCount > 0) {
        markCrmContactWhatsappRead(leadId, target.id)
          .then(() => loadConversations())
          .catch(() => {});
      }
    }
    if (onOpenedContact) onOpenedContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openContactId, loadingList]);

  const openConversation = async (conversation) => {
    setActiveContact(conversation);
    setSendError('');
    setDraft('');
    await loadThread(conversation.id);
    // Mark unread inbound as read, then refresh the list badge.
    if (conversation.unreadCount > 0) {
      try {
        await markCrmContactWhatsappRead(leadId, conversation.id);
        loadConversations();
      } catch (_) {
        /* non-fatal */
      }
    }
  };

  const backToList = () => {
    setActiveContact(null);
    setMessages([]);
    loadConversations();
  };

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || !hubId || sending || !activeContact) return;
    setSending(true);
    setSendError('');
    try {
      await sendCrmLeadWhatsapp(leadId, {
        contactId: Number(activeContact.id),
        hubId: Number(hubId),
        message,
      });
      setDraft('');
      await loadThread(activeContact.id);
    } catch (err) {
      setSendError(err.message || 'Could not send the message.');
    } finally {
      setSending(false);
    }
  };

  // Contacts that can be messaged but have no conversation yet — offered under
  // "New message" so an agent can start a fresh thread.
  const existingIds = new Set(conversations.map((c) => String(c.id)));
  const startableContacts = (contacts || []).filter(
    (c) => c.messageable && !existingIds.has(String(c.id))
  );

  const startConversation = (contact) => {
    setShowNewPicker(false);
    // Reuse the thread view; treat the picked contact as an empty conversation.
    setActiveContact({ ...contact, unreadCount: 0, lastMessage: null });
    setMessages([]);
    setDraft('');
    setSendError('');
    loadThread(contact.id);
  };

  // ---- List view ----
  if (!activeContact) {
    return (
      <div className="crm-wa-conv">
        <div className="crm-wa-conv-head">
          <span className="crm-wa-conv-head-title">Conversations</span>
          {startableContacts.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary crm-btn-xs"
              onClick={() => setShowNewPicker((v) => !v)}
            >
              {showNewPicker ? 'Cancel' : '+ New message'}
            </button>
          )}
        </div>

        {showNewPicker && (
          <div className="crm-wa-new-picker">
            <div className="crm-muted crm-wa-new-picker-label">Start a conversation with</div>
            {startableContacts.map((c) => (
              <button
                type="button"
                key={c.id}
                className="crm-wa-conv-item"
                onClick={() => startConversation(c)}
              >
                <span className="crm-wa-conv-avatar">{initial(c.name, c.phone)}</span>
                <span className="crm-wa-conv-main">
                  <span className="crm-wa-conv-name">{c.name || 'Unnamed contact'}</span>
                  <span className="crm-wa-conv-phone">{formatPhone(c.phone)}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {loadingList && conversations.length === 0 && (
          <div className="crm-muted" style={{ padding: '10px 0' }}>Loading conversations…</div>
        )}
        {listError && <div className="crm-form-error" style={{ margin: '8px 0' }}>{listError}</div>}
        {!loadingList && !listError && conversations.length === 0 && (
          <div className="crm-muted" style={{ padding: '10px 0' }}>
            No conversations yet.{startableContacts.length > 0 ? ' Use “New message” to start one.' : ''}
          </div>
        )}
        <div className="crm-wa-conv-list">
          {conversations.map((c) => (
            <button
              type="button"
              key={c.id}
              className="crm-wa-conv-item"
              onClick={() => openConversation(c)}
              title="Open conversation"
            >
              <span className="crm-wa-conv-avatar">{initial(c.name, c.phone)}</span>
              <span className="crm-wa-conv-main">
                <span className="crm-wa-conv-top">
                  <span className="crm-wa-conv-name">{c.name || 'Unnamed contact'}</span>
                  <span className="crm-wa-conv-time">{formatTime(c.lastMessageAt)}</span>
                </span>
                <span className="crm-wa-conv-sub">
                  <span className="crm-wa-conv-preview">
                    {(c.lastDirection === 'outbound' ? 'You: ' : '') + (c.lastMessage || '')}
                  </span>
                  {c.unreadCount > 0 && (
                    <span className="crm-wa-conv-badge">{c.unreadCount}</span>
                  )}
                </span>
                <span className="crm-wa-conv-phone">{formatPhone(c.phone)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---- Thread view ----
  return (
    <div className="crm-wa-thread">
      <div className="crm-wa-thread-header">
        <button type="button" className="crm-wa-back" onClick={backToList} aria-label="Back to conversations">←</button>
        <span className="crm-wa-conv-avatar">{initial(activeContact.name, activeContact.phone)}</span>
        <div className="crm-wa-thread-title">
          <div className="crm-wa-conv-name">{activeContact.name || 'Unnamed contact'}</div>
          <div className="crm-wa-conv-phone">{formatPhone(activeContact.phone)}</div>
        </div>
      </div>

      <div className="crm-wa-thread-body">
        {loadingThread && messages.length === 0 && (
          <div className="crm-muted" style={{ padding: '10px 0', textAlign: 'center' }}>Loading…</div>
        )}
        {threadError && <div className="crm-form-error" style={{ margin: '8px 0' }}>{threadError}</div>}
        {!loadingThread && !threadError && messages.length === 0 && (
          <div className="crm-muted" style={{ padding: '10px 0', textAlign: 'center' }}>No messages yet. Send the first one below.</div>
        )}
        {messages.map((m) => {
          const isInbound = m.direction === 'inbound';
          const footerBits = [];
          if (m.channel === 'group') footerBits.push('Group');
          else if (m.channel === 'direct') footerBits.push('Direct');
          if (m.hubNumber) footerBits.push(m.hubNumber);
          if (!isInbound && m.user) footerBits.push(m.user);
          return (
            <div key={m.id} className={`crm-chat-row crm-chat-row-${isInbound ? 'in' : 'out'}`}>
              <div className={`crm-chat-bubble crm-chat-bubble-${isInbound ? 'in' : 'out'}`}>
                <div className={`crm-chat-text${m.text ? '' : ' crm-chat-text-empty'}`}>
                  {m.text || '—'}
                </div>
                {footerBits.length > 0 && (
                  <div className="crm-chat-footer-meta">{footerBits.join(' · ')}</div>
                )}
                <div className="crm-chat-time">
                  <span>{formatTime(m.timestamp)}</span>
                  {!isInbound && m.status && (
                    <span className={`crm-chat-tick crm-chat-tick-${m.status}`}>
                      {m.status === 'sent' ? '✓✓' : m.status === 'failed' ? '⚠' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={threadEndRef} />
      </div>

      <div className="crm-wa-composer">
        {!activeContact.messageable ? (
          <div className="crm-wa-composer-blocked">This contact cannot be messaged from the CRM.</div>
        ) : (
          <>
            <div className="crm-wa-composer-bar">
              <select
                className="crm-wa-hub-select"
                value={hubId}
                onChange={(e) => { setHubId(e.target.value); setSendError(''); }}
                disabled={hubLinks.length === 0 || sending}
                aria-label="Send from hub"
              >
                <option value="">{hubLinks.length === 0 ? 'No hubs' : 'Hub…'}</option>
                {hubLinks.map((h) => (
                  <option key={h.id} value={h.hubId}>
                    {[h.name, h.city].filter(Boolean).join(' · ') || `Hub ${h.hubId}`}
                  </option>
                ))}
              </select>
              <textarea
                className="crm-wa-composer-input"
                rows={1}
                placeholder="Type a message…"
                value={draft}
                maxLength={4096}
                onChange={(e) => { setDraft(e.target.value); setSendError(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending}
              />
              <button
                type="button"
                className="crm-wa-send"
                onClick={handleSend}
                disabled={sending || !draft.trim() || !hubId}
                aria-label="Send message"
                title="Send (⌘/Ctrl+Enter)"
              >
                {sending ? (
                  <span className="crm-wa-send-spin">…</span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2z" fill="currentColor" />
                  </svg>
                )}
              </button>
            </div>
            {sendError && <div className="crm-form-error" style={{ marginTop: '6px' }}>{sendError}</div>}
            <div className="crm-wa-composer-hint">
              Sent from the hub number, never a personal number.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CrmWhatsappConversations;
